// Lista de problemas do admin, com o controle de liberação (isVisible).

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Files, Plus, Trash2, TriangleAlert } from "lucide-react";
import type { AdminProblemDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { Button } from "../../components/motion/button/base";
import { Input } from "../../components/motion/input";
import { Switch } from "../../components/motion/switch";
import { Table, type TableColumn } from "../../components/motion/table";
import { AlertMessage, useAlertConfirm } from "../../components/alerts";
import { EmptyState, PageHeader, tableHeight } from "../../components/domain.js";

const fields = {
  field: "rounded-xl bg-background",
  label: "px-0",
  input: "placeholder:text-muted-foreground",
};

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível concluir a operação.";

export function AdminProblemsPage(): React.JSX.Element {
  const confirmAlert = useAlertConfirm();
  const queryClient = useQueryClient();

  const problems = useQuery({
    queryKey: ["admin-problems"],
    queryFn: () => api.get<AdminProblemDto[]>("/admin/problems"),
  });

  const reload = () => queryClient.invalidateQueries({ queryKey: ["admin-problems"] });

  const toggleVisible = useMutation({
    mutationFn: ({ id, isVisible }: { id: string; isVisible: boolean }) =>
      api.patch<AdminProblemDto>(`/admin/problems/${id}`, { isVisible }),
    onSuccess: () => void reload(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/problems/${id}`),
    onSuccess: () => void reload(),
  });

  const list = problems.data ?? [];
  const busy = toggleVisible.isPending || remove.isPending;
  const actionError = toggleVisible.error ?? remove.error;

  const columns: TableColumn<AdminProblemDto>[] = [
    {
      key: "label",
      header: "Letra",
      width: "80px",
      cell: (p) => (
        <span className="grid size-8 place-items-center rounded-lg border border-border bg-background font-semibold">
          {p.label}
        </span>
      ),
    },
    {
      key: "title",
      header: "Título",
      cell: (p) => (
        <Link
          to={`/admin/problemas/${p.id}`}
          className="truncate font-medium text-accent hover:underline"
          title={p.title}
        >
          {p.title}
        </Link>
      ),
    },
    {
      key: "testCaseCount",
      header: "Casos",
      width: "110px",
      align: "center",
      cell: (p) =>
        p.testCaseCount === 0 ? (
          <span
            className="inline-flex items-center gap-1.5 text-destructive"
            title="Sem casos de teste: qualquer submissão vira falha do juiz"
          >
            <TriangleAlert size={14} aria-hidden />0
          </span>
        ) : (
          <span className="text-muted-foreground">{p.testCaseCount}</span>
        ),
    },
    {
      key: "isVisible",
      header: "Liberado",
      width: "110px",
      align: "center",
      cell: (p) => (
        <Switch
          checked={p.isVisible}
          disabled={busy}
          onCheckedChange={(isVisible: boolean) =>
            toggleVisible.mutate({ id: p.id, isVisible })
          }
          aria-label={`${p.isVisible ? "Ocultar" : "Liberar"} o problema ${p.label}`}
        />
      ),
    },
    {
      key: "actions",
      header: "",
      width: "90px",
      align: "right",
      cell: (p) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          aria-label={`Apagar o problema ${p.label}`}
          onClick={async () => {
            if (
              await confirmAlert(
                `Apagar o problema ${p.label}? A operação falha se já houver submissões — nesse caso, torne-o invisível.`,
              )
            ) {
              remove.mutate(p.id);
            }
          }}
        >
          <Trash2 size={15} className="text-destructive" aria-hidden />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-7">
      <PageHeader
        title="Problemas"
        description="Cadastre os problemas e libere-os para as equipes. Um problema só aparece na prova quando está liberado."
      />

      {actionError && <AlertMessage tone="error">{errorMessage(actionError)}</AlertMessage>}

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <section className="overflow-hidden rounded-2xl border border-border bg-card">
          <header className="flex items-center gap-2 border-b border-border px-5 py-4">
            <Files size={16} className="text-muted-foreground" aria-hidden />
            <h2 className="text-sm font-medium">
              {list.length} {list.length === 1 ? "problema" : "problemas"}
            </h2>
          </header>

          <Table
            data={list}
            columns={columns}
            getRowId={(p) => p.id}
            loading={problems.isPending}
            rowHeight={64}
            height={tableHeight(list.length)}
            className="rounded-none border-0 bg-transparent"
            emptyState={
              <EmptyState
                icon={Files}
                title="Nenhum problema cadastrado"
                description="Crie o primeiro problema no painel ao lado e depois adicione os casos de teste."
              />
            }
          />
        </section>

        <CreateProblem onCreated={reload} />
      </div>
    </div>
  );
}

function CreateProblem({ onCreated }: { onCreated: () => void }) {
  const [label, setLabel] = useState("");
  const [title, setTitle] = useState("");
  const [timeMs, setTimeMs] = useState("1000");
  const [memoryMb, setMemoryMb] = useState("256");

  const create = useMutation({
    mutationFn: () =>
      api.post<AdminProblemDto>("/admin/problems", {
        label: label.toUpperCase(),
        title: title.trim(),
        timeLimitMs: Number(timeMs),
        memoryLimitKb: Number(memoryMb) * 1024,
        statementMd: `## ${title.trim()}\n\nEscreva o enunciado aqui.`,
      }),
    onSuccess: () => {
      setLabel("");
      setTitle("");
      onCreated();
    },
  });

  return (
    <section className="h-fit rounded-2xl border border-border bg-card p-5">
      <h2 className="flex items-center gap-2 font-semibold">
        <Plus size={17} className="text-muted-foreground" aria-hidden />
        Novo problema
      </h2>
      <p className="mt-1 text-sm leading-6 text-muted-foreground">
        O problema nasce oculto. Libere-o quando a prova começar.
      </p>

      <div className="mt-5 space-y-4">
        <div className="grid grid-cols-[90px_1fr] gap-3">
          <Input
            id="problema-letra"
            label="Letra"
            placeholder="A"
            value={label}
            onChange={(v: string) => setLabel(v.toUpperCase().slice(0, 2))}
            disabled={create.isPending}
            classNames={fields}
          />
          <Input
            id="problema-titulo"
            label="Título"
            placeholder="Soma de dois números"
            value={title}
            onChange={setTitle}
            disabled={create.isPending}
            classNames={fields}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            id="problema-tempo"
            label="Tempo (ms)"
            type="number"
            value={timeMs}
            onChange={setTimeMs}
            disabled={create.isPending}
            classNames={fields}
          />
          <Input
            id="problema-memoria"
            label="Memória (MB)"
            type="number"
            value={memoryMb}
            onChange={setMemoryMb}
            disabled={create.isPending}
            classNames={fields}
          />
        </div>

        <AlertMessage>
          O limite de tempo é multiplicado por linguagem: Java e JavaScript 2×, Python 3×.
        </AlertMessage>

        {create.error && <AlertMessage tone="error">{errorMessage(create.error)}</AlertMessage>}

        <Button
          className="w-full rounded-xl"
          disabled={create.isPending || !label || title.trim().length === 0}
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Criando…" : "Criar problema"}
        </Button>
      </div>
    </section>
  );
}
